/**
 * Void Altar: estrutura na arena onde o player gasta Essence pra ficar mais forte.
 * Guarda o nível de cada upgrade (custo cresce por nível), gasta do inventário e
 * aplica o efeito no player. Também expõe hooks de save (níveis).
 *
 * Preparado (não implementado): species mastery, diminishing returns, area scaling,
 * enemy tier — o custo já é uma fórmula central e configurável.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { makeLabel } from "./StructureLabel";
import { crystalStdMat, darkStoneMat, energyMat, stoneMat } from "./Materials";
import type { AltarUpgradeDef } from "../config/GameConfig";
import { type EssenceType, ESSENCE_TYPES } from "../essence/EssenceTypes";
import type { EssenceInventory } from "../essence/EssenceInventory";
import { events } from "../core/Events";

export class VoidAltar {
  readonly levels: Record<EssenceType, number> = {
    power: 0,
    vitality: 0,
    agility: 0,
    fortitude: 0,
    ferocity: 0,
  };

  private readonly cfg = GameConfig.altar;
  private readonly pos = new THREE.Vector3();
  private readonly group = new THREE.Group();
  private crystal!: THREE.Mesh;
  private bob = 0;

  constructor(
    scene: THREE.Scene,
    private readonly inventory: EssenceInventory,
  ) {
    this.pos.set(this.cfg.position.x, 0, this.cfg.position.z);
    this.buildMesh();
    this.group.position.copy(this.pos);
    scene.add(this.group);
  }

  get position(): THREE.Vector3 {
    return this.pos;
  }

  /** Mostra/esconde o altar (é da Região 1). */
  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  isPlayerNear(playerPos: THREE.Vector3): boolean {
    return playerPos.distanceTo(this.pos) <= this.cfg.interactRadius;
  }

  /** custo = round(base × growth^nível) */
  costFor(key: EssenceType): number {
    return Math.round(this.cfg.cost.base * Math.pow(this.cfg.cost.growth, this.levels[key]));
  }

  defFor(key: EssenceType): AltarUpgradeDef {
    return this.cfg.upgrades.find((u) => u.key === key)!;
  }

  levelOf(key: EssenceType): number {
    return this.levels[key];
  }

  canAfford(key: EssenceType): boolean {
    return this.inventory.get(key) >= this.costFor(key);
  }

  buy(key: EssenceType): boolean {
    const cost = this.costFor(key);
    if (!this.inventory.spend(key, cost)) return false;
    this.levels[key] += 1;
    // O efeito é aplicado pelo Game via recálculo (evento altar:upgraded) — sem drift.
    events.emit("altar:upgraded", { key, level: this.levels[key] });
    return true;
  }

  update(dt: number): void {
    this.bob += dt;
    this.crystal.rotation.y += dt * 0.8;
    this.crystal.position.y = 0.95 + Math.sin(this.bob * 1.7) * 0.07;
  }

  // ----- save -----
  snapshot(): Record<EssenceType, number> {
    return { ...this.levels };
  }

  /** Restaura níveis (o efeito é recalculado pelo Game a partir de levelOf). */
  applyLoadedLevels(data: Partial<Record<EssenceType, number>> | undefined): void {
    const MAX = 999; // teto sanitário contra save adulterado
    for (const key of ESSENCE_TYPES) {
      this.levels[key] = Math.min(MAX, Math.max(0, Math.floor(data?.[key] ?? 0)));
    }
  }

  reset(): void {
    for (const key of ESSENCE_TYPES) this.levels[key] = 0;
  }

  /**
   * 10.6A — ALTAR BAIXO. Antes era um pilar de 2,4m com um cristal roxo em cima (a "tenda
   * rosa" da base): com a câmera de cima ele TAPAVA o Caçador — quem chegava perto sumia
   * atrás dele. Agora é um santuário rente ao chão (altura total ~1,0m): plataforma
   * octogonal + runas brilhantes + 4 lascas curtas + um cristal pequeno flutuando baixo.
   * Continua sendo o mesmo altar (mesmo raio de interação, mesmos upgrades), só que agora
   * NUNCA esconde o personagem.
   */
  private buildMesh(): void {
    // 10.9BQ — tudo da biblioteca (nada aqui é animado por material: só o cristal gira/flutua)
    const base = new THREE.Mesh(new THREE.CylinderGeometry(1.75, 1.95, 0.26, 8), darkStoneMat());
    base.position.y = 0.13;
    base.receiveShadow = true;
    this.group.add(base);

    // runa luminosa embutida na plataforma (leitura de "estação" vista de cima)
    const rune = new THREE.Mesh(new THREE.RingGeometry(0.85, 1.5, 8), energyMat(0x9d7bff, "crystal"));
    rune.rotation.x = -Math.PI / 2;
    rune.position.y = 0.28;
    this.group.add(rune);

    // 4 lascas curtas na borda (silhueta do altar sem virar parede) — pequenas: sem sombra
    const shardMat = stoneMat(0x6a5aa0);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const shard = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.62, 4), shardMat);
      shard.position.set(Math.cos(a) * 1.35, 0.57, Math.sin(a) * 1.35);
      this.group.add(shard);
    }

    this.crystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.34, 0), crystalStdMat(0x9d7bff, 0x7c4dff, 1.6));
    this.crystal.position.y = 0.95;
    this.group.add(this.crystal);

    // 10.9BP — PointLight próprio REMOVIDO: o hub só mantém a luz do núcleo do Santuário
    // (menos luzes = mais barato no mobile); o cristal emissivo já dá a leitura de brilho.

    // 10.9B — rótulo flutuante
    this.group.add(makeLabel("✦ Altar do Void", "#9d7bff", 3.2));
  }
}
